import React, { useCallback, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { Box } from "@mui/material";
import { Plus, Edit, Trash2, Ship, ArrowRight } from "lucide-react";
import DeleteConfirmationPopup from "./DeleteConfirmation";
import AddEditVesselModal from "./AddEditVesselModal";
import VesselVoyages from "./VesselVoyages";
import { getVesselVoyagesByVesselId } from "../../../services/vesselService";
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  colorSchemeLight,
} from "ag-grid-community";

const vesselGridTheme = themeQuartz.withPart(colorSchemeLight).withParams({
  backgroundColor: "#ffffff",
  foregroundColor: "#1f2937",
  accentColor: "#3b82f6",
  headerTextColor: "#ffffff",
  headerBackgroundColor: "#1a325d",
  oddRowBackgroundColor: "#f9fafb",
  chromeBackgroundColor: "#f9fafb",
});

ModuleRegistry.registerModules([AllCommunityModule]);

const VesselTable = ({ vessels, onEdit, onDelete, onVesselAdded }) => {
  const [deletePopup, setDeletePopup] = useState({
    isOpen: false,
    vessel: null,
  });
  const [voyages, setVoyages] = useState([]);
  const [addVesselModalOpen, setAddVesselModalOpen] = useState(false);
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [modalMode, setModalMode] = useState("add");
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("vessels");
  const [selectedVesselForVoyages, setSelectedVesselForVoyages] =
    useState(null);

  const gridRef = useRef();

  const fetchVoyages = async (vesselId) => {
    if (!vesselId) {
      console.warn("No vesselId found for current vessel!");
      setVoyages([]);
      return;
    }
    setLoading(true);
    try {
      const response = await getVesselVoyagesByVesselId(vesselId);
      const voyagesData = response?.data?.data || [];
      console.log("voyagesData: ", voyagesData);
      setVoyages(voyagesData);
    } catch (error) {
      console.error("Failed to fetch voyages:", error);
      setVoyages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (vessel) => {
    setSelectedVessel(vessel);
    setModalMode("edit");
    setAddVesselModalOpen(true);
  };

  const handleDeleteClick = (vessel) => {
    setDeletePopup({ isOpen: true, vessel });
  };

  const handleDeleteConfirm = () => {
    if (deletePopup.vessel) {
      onDelete?.(deletePopup.vessel._id);
      setDeletePopup({ isOpen: false, vessel: null });
    }
  };

  const handleViewVoyages = (vessel) => {
    console.log("Viewing voyages for vessel:", vessel);
    setSelectedVesselForVoyages(vessel);
    setViewMode("voyages");
    fetchVoyages(vessel._id);
  };

  const handleBackToVessels = () => {
    setViewMode("vessels");
    setSelectedVesselForVoyages(null);
    setVoyages([]);
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value}
          </span>
        ),
      },
      {
        headerName: "IMO No",
        field: "imoNumber",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value}
          </span>
        ),
      },
      {
        headerName: "Vessel Class",
        field: "vesselClass",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value}
          </span>
        ),
      },
      {
        headerName: "Company",
        field: "company",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value.name}
          </span>
        ),
      },
      {
        headerName: "Voyages",
        field: "_id",
        cellRenderer: (params) => (
          <button
            onClick={() => handleViewVoyages(params.data)}
            className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 hover:shadow-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            View Voyages
          </button>
        ),
      },
      {
        headerName: "Classification",
        field: "classification",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value}
          </span>
        ),
      },
      {
        headerName: "Vessel Code",
        field: "vesselCode",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value}
          </span>
        ),
      },
      {
        headerName: "Call Sign",
        field: "callSign",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value}
          </span>
        ),
      },
      {
        headerName: "Port Of Registry",
        field: "portOfRegistry",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value}
          </span>
        ),
      },
      {
        headerName: "Flag",
        field: "flag",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value}
          </span>
        ),
      },
      {
        headerName: "Year Built",
        field: "yearBuilt",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value}
          </span>
        ),
      },
      {
        headerName: "Builder",
        field: "builderInfo",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value}
          </span>
        ),
      },
      {
        headerName: "GT",
        field: "grossTonnage",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value}
          </span>
        ),
      },
      {
        headerName: "NT",
        field: "netTonnage",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value}
          </span>
        ),
      },
      {
        headerName: "DWT",
        field: "deadweight",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value}
          </span>
        ),
      },
      {
        headerName: "Accommodation",
        field: "accommodation",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value}
          </span>
        ),
      },
      {
        headerName: "Dimensions (L/B/D/D)",
        field: "dimensions",
        cellRenderer: (params) => {
          const d = params.value || {};
          const dimensionsText = `${d.loa || "—"} / ${d.boa || "—"} / ${
            d.depth || "—"
          } / ${d.draft || "—"}`;

          return (
            <span className="text-gray-700 text-sm font-medium">
              {dimensionsText}
            </span>
          );
        },
      },
      {
        headerName: "Actions",
        width: 140,
        pinned: "right",
        suppressMenu: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
        cellRenderer: (params) => (
          <div className="flex items-center gap-2 h-full">
            <button
              onClick={() => handleEdit(params.data)}
              className="group p-2 rounded-full bg-white shadow-md hover:bg-blue-100 transition-all duration-200 border border-blue-200 hover:shadow-blue-300"
            >
              <Edit className="w-4 h-4 text-blue-600 group-hover:scale-110 group-hover:text-blue-700 transition-all" />
            </button>
            <button
              onClick={() => handleDeleteClick(params.data)}
              className="group p-2 rounded-full bg-white shadow-md hover:bg-rose-100 transition-all duration-200 border border-rose-200 hover:shadow-rose-300"
            >
              <Trash2 className="w-4 h-4 text-rose-500 group-hover:scale-110 group-hover:text-rose-700 transition-all" />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // Render Vessel Voyages view if in voyages mode
  if (viewMode === "voyages" && selectedVesselForVoyages) {
    return (
      <Box>
        <div className="flex items-center justify-between mb-4">
          <h2 className="flex items-center gap-2 text-[24px] font-semibold text-slate-800 tracking-tight">
            <Ship className="w-6 h-6 text-[#27ade3]" strokeWidth={2} />
            Voyages for {selectedVesselForVoyages.name}
          </h2>
          <button
            onClick={handleBackToVessels}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-tr from-[#1a325d] to-[#27ade3] shadow-md hover:scale-105 transition-transform rounded-lg"
          >
            Back to Vessels
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <VesselVoyages vesselVoyages={voyages} loading={loading} />
      </Box>
    );
  }

  // Render Vessels table view (default)
  return (
    <Box sx={{}}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="flex items-center gap-2 text-[24px] font-semibold text-slate-800 tracking-tight">
          <Ship className="w-6 h-6 text-[#27ade3]" strokeWidth={2} />
          Vessel Management
        </h2>

        <button
          onClick={() => setAddVesselModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-tr from-[#1a325d] to-[#27ade3] shadow-md hover:scale-105 transition-transform rounded-lg"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          Add Vessel
        </button>
      </div>

      {/* Table Container */}
      <Box
        className="ag-theme-quartz relative"
        sx={{
          width: "100%",
          overflow: "hidden",
        }}
      >
        <AgGridReact
          ref={gridRef}
          theme={vesselGridTheme}
          rowData={vessels}
          columnDefs={columnDefs}
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 20, 50]}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: true,
          }}
          domLayout="autoHeight"
          rowHeight={45}
          onGridReady={() => {
            setTimeout(() => {
              const header = document.querySelector(".ag-header");
              if (header) {
                header.style.background =
                  "linear-gradient(to top right, #1a325d, #27ade3)";
                header.style.color = "#ffffff";
              }
            }, 0);
          }}
          suppressHorizontalScroll={false}
        />
      </Box>

      {/* Modals */}
      <DeleteConfirmationPopup
        isOpen={deletePopup.isOpen}
        onClose={() => setDeletePopup({ isOpen: false, vessel: null })}
        onConfirm={handleDeleteConfirm}
        userName={deletePopup.vessel ? `${deletePopup.vessel.name}` : ""}
      />

      {addVesselModalOpen && (
        <AddEditVesselModal
          open={addVesselModalOpen}
          vessels={vessels}
          onClose={() => {
            setAddVesselModalOpen(false);
            setSelectedVessel(null);
            setModalMode("add");
          }}
          onSubmit={() => {
            setAddVesselModalOpen(false);
            setSelectedVessel(null);
            setModalMode("add");
            onVesselAdded?.();
          }}
          mode={modalMode}
          vessel={selectedVessel}
        />
      )}
    </Box>
  );
};

export default VesselTable;
